import { useRouter } from 'next/router'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Fit } from '../../../../../../brunswick/constants'
import { nullSizeChart } from '../../../../../../brunswick/constants'
import { EmbroideryService } from '../../../../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../../../../containers/EmbroideryService/EmbroideryModalService'
import { useColorsWithUnavailable } from '../../../../../../lib/hooks/useColorsWithUnavailable'
import { useGoogleTagManager } from '../../../../../../lib/hooks/useGoogleTagManager'
import { useProductGroupDetails } from '../../../../../../lib/hooks/useProductGroupDetails'
import {
  areFitsEquivalent,
  deriveAvailableProducts,
  formatPrettyTitlePG,
} from '../../../../../../lib/utils'
import { deriveSizes } from '../../../../../../lib/utils'
import { ProductAsset } from '../../../../../../types/graphql'
import { ProductGroupWithProductAvailability, Variant } from '../../../../../../types/graphql'
import { EmbroideryModal } from '../../../../../embroidery/EmbroideryModal/EmbroideryModal'
import ProductDetailsModal from '../../../../../modals/ProductDetailsModal'
import { getItemActiveColor } from '../../../KitPage/KitDetails/utils'
import { useFetchProductPageData } from '../../../ProductPage/hooks/useFetchProductPageData'
import { useProductPageEmbroiderySettings } from '../../../ProductPage/hooks/useProductPageEmbroiderySettings'

import { KitDetailsSection } from './KitDetailsSection'
import KitProductListItemSkeleton from './KitProductListItemSkeleton'
import { Container } from './styles'

export interface KitProductListItemProps {
  defaultColors: Record<string, string> | null
  index: number
  isKitFinalSale: boolean
  kitHandle: string
  productGroup: ProductGroupWithProductAvailability | null
  updateSelectedVariants: (variant: Variant, index: number) => void
  hideProductDetailsModal: boolean
}

export const KitProductListItem: FC<KitProductListItemProps> = ({
  defaultColors,
  index,
  isKitFinalSale,
  kitHandle,
  productGroup,
  updateSelectedVariants,
  hideProductDetailsModal,
}) => {
  const { query } = useRouter()

  // A kit might contain the same item multiple times, so we need an index
  const queryColorKey = `kit-product-${index}-color`
  const querySizeKey = `kit-product-${index}-size`
  const queryFitKey = `kit-product-${index}-fit`

  // Size
  const querySizeValue = query[querySizeKey] as string | undefined

  // Fit
  const queryFitValue = query[queryFitKey] as string | undefined
  const selectedFit = queryFitValue ?? Fit.REGULAR

  // Color
  const queryGlobalColorValue = query.color as string | undefined
  const globalColor = queryGlobalColorValue
  const queryColorValue = query[queryColorKey] as string | undefined
  const selectedColor = getItemActiveColor(
    productGroup,
    queryColorValue,
    globalColor,
    defaultColors
  )

  // TODO: This is another example of TypeScript issues. It shouldn't be
  // possible for us to be missing the product group handle unless we had
  // experienced a 4XX or 5XX code for this page.
  const productGroupHandle = productGroup?.handle ?? ''

  // TODO: We should not be querying the full PDP info if we only need a piece of it.
  // This query pulls lots of group data beyond the featured product......
  const { featuredProduct, featuredProductStatus } = useFetchProductPageData({
    productGroupHandle: productGroupHandle,
    featuredProductColor: selectedColor,
    featuredProductFit: selectedFit,
  })

  const coloredAndFitVariants = useMemo(() => {
    return featuredProduct?.variants ?? []
  }, [featuredProduct?.variants])

  const sizes = useMemo(() => {
    return deriveSizes(coloredAndFitVariants, productGroup?.sizes ?? [])
  }, [coloredAndFitVariants, productGroup?.sizes])

  const { pushProductClick, pushProductDetailsImpression } = useGoogleTagManager()

  const availableProducts = useMemo(() => {
    if (productGroup == null || !productGroup.productsForAvailability) return {}
    return deriveAvailableProducts(productGroup.productsForAvailability)
  }, [productGroup])

  const productGroupDetails = useProductGroupDetails({
    availableProducts,
    coloredAndFitVariants,
    currentColorKey: selectedColor,
    defaultVariantFitKey: productGroup?.defaultVariant?.rawFit,
    productGroup,
    productGroupFitsKeys: productGroup?.rawFits,
    selectedFitKey: selectedFit,
    selectedSizeName: querySizeValue,
  })

  // Wait to access the new results until the query finishes.
  // We show stale results while loading the next chunk of info.
  const productGroupDetailsRef = useRef(productGroupDetails)
  if (featuredProductStatus === 'resolved' || featuredProductStatus === 'rejected') {
    productGroupDetailsRef.current = productGroupDetails
  }

  const {
    availableFitsKeys,
    coloredVariant,
    currentFitKey,
    currentSize: productGroupCurrentSize,
    fitNames,
  } = productGroupDetailsRef.current
  const selectedSize = productGroupCurrentSize

  const fitKeys = useMemo(() => {
    return availableFitsKeys.length === 1 && availableFitsKeys[0] === selectedFit
      ? []
      : availableFitsKeys
  }, [availableFitsKeys, selectedFit])

  const selectedVariant =
    coloredAndFitVariants.length === 1
      ? coloredAndFitVariants[0]
      : coloredAndFitVariants.find(product => product.size === selectedSize?.shorthand)

  useEffect(() => {
    if (!selectedVariant) return
    updateSelectedVariants(selectedVariant, index)
    const numericShopifyVariantIdMatch = selectedVariant?.shopifyId.match(/\d+$/)
    if (!numericShopifyVariantIdMatch) return
    pushProductClick({ variant: selectedVariant })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant])

  useEffect(() => {
    if (!coloredVariant) return
    pushProductDetailsImpression({ variant: coloredVariant })
  }, [coloredVariant, pushProductDetailsImpression])

  const [shouldDisplayDetailsModal, setShouldDisplayDetailsModal] = useState<boolean>(false)
  const openDetailsModal = useCallback(() => {
    setShouldDisplayDetailsModal(true)
  }, [])
  const closeDetailsModal = useCallback(() => {
    setShouldDisplayDetailsModal(false)
  }, [])

  const fittedVariants: ReadonlyArray<Variant> = useMemo(() => {
    return (
      productGroup?.products
        .map(product => product.variants ?? [])
        .flat()
        .filter(o => areFitsEquivalent(o.rawFit, currentFitKey)) ?? []
    )
  }, [currentFitKey, productGroup?.products])

  const colors = useMemo(() => {
    return productGroup?.colors ?? []
  }, [productGroup?.colors])

  const { colorsWithUnavailable, isCurrentColorCompletelySoldOut } = useColorsWithUnavailable({
    colors,
    colorKey: selectedColor,
    variants: fittedVariants,
    sizeName: selectedSize?.shorthand ?? querySizeValue,
  })

  const activeProduct =
    productGroup?.products.find(product => product.handle === coloredVariant?.handle) ??
    productGroup?.products[0]

  const sizeChart =
    productGroup?.sizeChart?.name === nullSizeChart ? undefined : productGroup?.sizeChart

  // Note: handle !== activeProductHandle
  const activeProductId = activeProduct?.id ?? ''
  const activeProductHandle = activeProduct?.handle ?? ''
  const activeProductShopifyId = activeProduct?.shopifyId ?? ''

  const thumbnailImage =
    activeProduct?.images?.nodes.find(({ kind }) => kind === 'ghost') ??
    activeProduct?.images?.nodes[0]

  const prettyTitle: string = formatPrettyTitlePG(productGroup)

  const currentProductColor = productGroup?.colors.find(o => o.rawName === selectedColor)

  const isProductEmbroiderable: boolean = !!featuredProduct?.embroidery?.length

  const isFinalSale = activeProduct?.finalSale || isKitFinalSale

  const productImagesWithDefaultFlatlays: ReadonlyArray<ProductAsset> = useMemo(() => {
    return (activeProduct?.images?.nodes ?? []).concat(productGroup?.embroideryImages ?? [])
  }, [activeProduct?.images?.nodes, productGroup?.embroideryImages])

  const { embroideryOptions, embroideryVariantsByKind } = useProductPageEmbroiderySettings({
    embroidery: featuredProduct?.embroidery || [],
  })

  if (!featuredProduct || !productGroup || !activeProduct)
    return (
      <Container>
        <KitProductListItemSkeleton />
      </Container>
    )

  return (
    <EmbroideryService.Provider
      initialState={{
        selectedSize: selectedVariant ? selectedVariant.size : null,
        embroideryOptions,
        productImagesWithDefaultFlatlays,
        productColor: currentProductColor,
        productCategory: selectedVariant?.category,
        embroideryVariantsByKind,
      }}
      key={kitHandle}
    >
      <EmbroideryModalService.Provider>
        <Container faded={featuredProductStatus === 'pending'}>
          <EmbroideryModal embroideryOptions={embroideryOptions} />
          {!hideProductDetailsModal && (
            <ProductDetailsModal
              closeModal={closeDetailsModal}
              isOpen={shouldDisplayDetailsModal}
              product={activeProduct}
              productGroupRtfFeatures={productGroup.rtfFeatures}
              title={`${prettyTitle} ${productGroup.description}`}
            />
          )}

          <KitDetailsSection
            activeProductId={activeProductId}
            activeProductHandle={activeProductHandle}
            activeProductShopifyId={activeProductShopifyId}
            coloredAndFit={coloredAndFitVariants}
            isCurrentColorCompletelySoldOut={isCurrentColorCompletelySoldOut}
            keyColor={queryColorKey}
            keyFit={queryFitKey}
            keySize={querySizeKey}
            kitHandle={kitHandle}
            openDetailsModal={openDetailsModal}
            noProductDetailsModal={hideProductDetailsModal}
            selectedVariant={selectedVariant}
            sizeChart={sizeChart}
            sizes={sizes}
            colors={colorsWithUnavailable}
            description={productGroup.description ?? ''}
            embroiderable={isProductEmbroiderable}
            finalSale={isFinalSale}
            fitKeys={fitKeys}
            fitNames={fitNames}
            handle={activeProduct?.handle || productGroup.defaultVariant.handle}
            id={selectedVariant?.shopifyId || productGroup.id}
            index={index}
            isProductWaitlistable={activeProduct.waitlistable}
            image={thumbnailImage}
            productGroupHandle={productGroup.handle}
            selectedColorKey={selectedColor}
            selectedFitKey={currentFitKey}
            selectedSize={selectedSize}
            updateSelectedVariants={updateSelectedVariants}
            title={prettyTitle}
          />
        </Container>
      </EmbroideryModalService.Provider>
    </EmbroideryService.Provider>
  )
}

export default KitProductListItem
